// Urban Forest Climate Resiliency Master Plan — Goals, Objectives & Actions
// Adopted February 2022 (Action Plan, Chapter 8).
// Source: "UrbanForestry_Goals_and_CARP_Crosswalk.docx" (crosswalk to CARP, June 2026).
//
// Structure mirrors the CARP: Category -> Goal -> Objective (= CARP Strategy) -> Action.
// `partOfCarp` / `carpRef` mark items that also appear in the CARP; `partOfOsrp` /
// `osrpRef` mark items that also appear in the 2025 Open Space & Recreation Plan.
// Objective status is intentionally NOT tracked here — there is no reliable record
// of the plan's implementation status, so the page shows no status indicators.

export const urbanForestryData = {
  "adoptedDate": "February 2022",
  "sourceNote": "Extracted from the Brookline Urban Forest Climate Resiliency Master Plan (adopted February 2022; Action Plan, Chapter 8) and cross-referenced to the Climate Action & Resiliency Plan (CARP) Implementation Plan (June 2026). This is an unofficial reference compiled for comparison purposes.",
  "introduction": {
    "purpose": [
      "This page enumerates the goals, objectives, and action steps of the Brookline Urban Forest Climate Resiliency Master Plan, organized to mirror the structure of the Climate Action & Resiliency Plan (CARP).",
      "The Urban Forestry plan calls its mid-level grouping an \"Objective\"; CARP calls the equivalent level a \"Strategy.\" They play the same role, so each Urban Forestry Objective is presented here at the level a CARP Strategy would occupy, with its Actions beneath it."
    ],
    "carpOverlap": [
      "Nearly the entire Urban Forestry plan corresponds to a single CARP goal area — NSCW 1, \"Increase urban tree canopy coverage\" — with spillover into green-infrastructure (NSCW 3), urban-heat (RAR 2), and funding (CC) strategies. CARP compresses the urban forest into about one goal and two strategies, while this plan expands it into 11 goals, 28 objectives, and 68 actions.",
      "Objectives and actions that also appear in the CARP are marked with a \"Part of CARP\" label and the corresponding CARP strategy or action identifier.",
      "Objectives and actions that also appear in the 2025 Open Space & Recreation Plan (OSRP) are marked with an \"OSRP\" label and the corresponding OSRP action identifier."
    ],
    "abbreviations": [
      "POS = Parks & Open Space Division; PCD = Planning & Community Development Dept.; DPW = Dept. of Public Works; SB = Select Board; SBCTP = Select Board’s Committee on Tree Protection; TPC = Tree Planting Committee; IT = Information Technology; ODI = Office of Diversity & Inclusion."
    ]
  },
  "categories": [
    {
      "id": "I",
      "name": "Urban Forest Development",
      "goals": [
        {
          "id": "I.A.1",
          "sectionLetter": "A",
          "sectionName": "Grow the Urban Forest",
          "title": "Increase urban tree canopy coverage town-wide",
          "objectives": [
            {
              "id": "I.A.1.1",
              "num": "1",
              "title": "Mitigate the effects of climate change",
              "actions": [
                {
                  "id": "I.A.1.1.a",
                  "description": "Increase planting where the urban heat island effect is greatest (see areas/streets, pp.107 & 114).",
                  "responsible": "POS, TPC",
                  "years": "2022–2032",
                  "partOfCarp": true,
                  "carpRef": "NSCW 1.1"
                },
                {
                  "id": "I.A.1.1.b",
                  "description": "Adapt the tree species plant list based on current and future hardiness, biodiversity, stormwater absorption, and air quality (pp.104 & 128).",
                  "responsible": "POS",
                  "years": "2022"
                }
              ],
              "partOfCarp": true,
              "carpRef": "NSCW 1.1",
              "partOfOsrp": true,
              "osrpRef": "RP 4.3.3"
            },
            {
              "id": "I.A.1.2",
              "num": "2",
              "title": "Increase the benefits of trees (ecological, human health, resilience)",
              "actions": [
                {
                  "id": "I.A.1.2.a",
                  "description": "Select tree species suited to reduce air pollution.",
                  "responsible": "POS",
                  "years": "2023–2032"
                },
                {
                  "id": "I.A.1.2.b",
                  "description": "Improve biodiversity with varied tree species.",
                  "responsible": "POS",
                  "years": "2023–2032"
                },
                {
                  "id": "I.A.1.2.c",
                  "description": "Create connected habitat corridors, coordinated with the 2018 Open Space & Recreation Plan.",
                  "responsible": "Conservation Commission, TPC, POS",
                  "years": "2025",
                  "partOfOsrp": true,
                  "osrpRef": "RP 5.1.3"
                },
                {
                  "id": "I.A.1.2.d",
                  "description": "Replace invasive species (e.g., Norway maple, tree of heaven) when conditions allow.",
                  "responsible": "POS",
                  "years": "ongoing"
                }
              ]
            },
            {
              "id": "I.A.1.3",
              "num": "3",
              "title": "Provide equitable tree canopy coverage",
              "actions": [
                {
                  "id": "I.A.1.3.a",
                  "description": "Focus new planting in North Brookline, where areas are below the Town canopy average.",
                  "responsible": "POS",
                  "years": "2022–2032"
                },
                {
                  "id": "I.A.1.3.b",
                  "description": "Prioritize planting in environmental justice neighborhoods, affordable/public housing, elderly areas, and transit stops.",
                  "responsible": "POS",
                  "years": "2022–2027",
                  "partOfCarp": true,
                  "carpRef": "NSCW 1.1.a"
                }
              ],
              "partOfCarp": true,
              "carpRef": "NSCW 1.1",
              "partOfOsrp": true,
              "osrpRef": "RP 4.3.3"
            },
            {
              "id": "I.A.1.4",
              "num": "4",
              "title": "Reverse the trend of tree canopy loss (2014–2020)",
              "actions": [
                {
                  "id": "I.A.1.4.a",
                  "description": "Increase staffing and funding to support annual planting of 450 trees (Funding, p.151).",
                  "responsible": "POS",
                  "years": "2022–2032",
                  "partOfCarp": true,
                  "carpRef": "NSCW 1.1"
                },
                {
                  "id": "I.A.1.4.b",
                  "description": "Continue to infill-plant and replace dead or storm-damaged trees town-wide.",
                  "responsible": "TPC, POS",
                  "years": "2022–2032",
                  "partOfCarp": true,
                  "carpRef": "NSCW 1.1.d"
                },
                {
                  "id": "I.A.1.4.c",
                  "description": "Procure new LiDAR data and re-analyze canopy in 3–5 years to measure progress.",
                  "responsible": "POS, IT",
                  "years": "2024–2026",
                  "partOfCarp": true,
                  "carpRef": "NSCW 1.1.f"
                },
                {
                  "id": "I.A.1.4.d",
                  "description": "Consider/execute street-specific recommendations for public ways.",
                  "responsible": "DPW, PCD",
                  "years": "2022–2032",
                  "partOfCarp": true,
                  "carpRef": "NSCW 1.1.e"
                },
                {
                  "id": "I.A.1.4.e",
                  "description": "Coordinate with MassDOT, MBTA, and DCR on planting strategies and soil volume.",
                  "responsible": "DPW",
                  "years": "2022–2032"
                }
              ],
              "partOfCarp": true,
              "carpRef": "NSCW 1.1"
            },
            {
              "id": "I.A.1.5",
              "num": "5",
              "title": "Encourage tree planting on private property",
              "actions": [
                {
                  "id": "I.A.1.5.a",
                  "description": "Further advertise the Back-of-Sidewalk Tree Planting Program (partner with non-profits; social media, newspapers, fliers).",
                  "responsible": "TPC, POS, Allied Non-Profits",
                  "years": "2022–2023",
                  "partOfCarp": true,
                  "carpRef": "NSCW 1.2.c"
                },
                {
                  "id": "I.A.1.5.b",
                  "description": "Educate property owners about the importance of planting new trees.",
                  "responsible": "PCD, TPC, Conservation Commission, POS",
                  "years": "ongoing"
                },
                {
                  "id": "I.A.1.5.c",
                  "description": "Share canopy expansion goals with project proponents during design/development review.",
                  "responsible": "PCD",
                  "years": "2023"
                },
                {
                  "id": "I.A.1.5.d",
                  "description": "Promote tree planting to mitigate the urban heat island.",
                  "responsible": "PCD",
                  "years": "2024"
                }
              ],
              "partOfCarp": true,
              "carpRef": "NSCW 1.2",
              "partOfOsrp": true,
              "osrpRef": "M 2.2.2"
            }
          ]
        }
      ]
    },
    {
      "id": "II",
      "name": "Urban Forest Protection",
      "goals": [
        {
          "id": "II.A.1",
          "sectionLetter": "A",
          "sectionName": "Protect the Urban Forest",
          "title": "Protect existing trees throughout the Town",
          "objectives": [
            {
              "id": "II.A.1.1",
              "num": "1",
              "title": "Maintain existing public trees so they can continue to grow",
              "actions": [
                {
                  "id": "II.A.1.1.a",
                  "description": "Increase Forestry Sector funding for pruning/care; by 2032 seek to raise the forestry budget to $1,350,319.",
                  "responsible": "SB, POS",
                  "years": "2023–2032"
                },
                {
                  "id": "II.A.1.1.b",
                  "description": "Maintain the inventory of trees overhanging the public way (PeopleGIS).",
                  "responsible": "POS",
                  "years": "ongoing"
                },
                {
                  "id": "II.A.1.1.c",
                  "description": "Conduct a stem-by-stem inventory of park and Town-ground trees.",
                  "responsible": "POS",
                  "years": "2025"
                },
                {
                  "id": "II.A.1.1.d",
                  "description": "Document and celebrate heritage trees (≥32\" diameter or historically significant).",
                  "responsible": "TPC, POS",
                  "years": "2025"
                }
              ],
              "partOfOsrp": true,
              "osrpRef": "RP 6.2.2"
            }
          ]
        },
        {
          "id": "II.A.2",
          "sectionLetter": "A",
          "sectionName": "Protect the Urban Forest",
          "title": "Consider private tree protection in regulatory processes and planning",
          "objectives": [
            {
              "id": "II.A.2.1",
              "num": "1",
              "title": "Incorporate canopy-specific guidelines into Zoning Bylaw §5.09 'Design Review'",
              "actions": [
                {
                  "id": "II.A.2.1.a",
                  "description": "Convene a working group to develop canopy-related guidelines under §5.09.",
                  "responsible": "POS, PCD, SBCTP",
                  "years": "2022–2023"
                },
                {
                  "id": "II.A.2.1.b",
                  "description": "Request the Planning Board formally adopt the guidelines for the 'Design Review' process.",
                  "responsible": "POS, PCD",
                  "years": "2023–2024"
                },
                {
                  "id": "II.A.2.1.c",
                  "description": "After a trial period, reconvene to assess and develop specific requirements.",
                  "responsible": "POS, PCD, SBCTP",
                  "years": "2025–2026"
                },
                {
                  "id": "II.A.2.1.d",
                  "description": "Draft a warrant article to amend the Zoning Bylaw to include canopy-specific requirements.",
                  "responsible": "POS, PCD, SBCTP",
                  "years": "2026–2028"
                }
              ],
              "partOfCarp": true,
              "carpRef": "NSCW 3.1",
              "partOfOsrp": true,
              "osrpRef": "M 2.1.4"
            },
            {
              "id": "II.A.2.2",
              "num": "2",
              "title": "Encourage tree preservation/planting on private property through regulatory review",
              "actions": [
                {
                  "id": "II.A.2.2.a",
                  "description": "Convene a working group on triggers (impervious surface/footprint); develop precinct-specific canopy standards.",
                  "responsible": "POS, PCD, SBCTP",
                  "years": "2022–2024"
                },
                {
                  "id": "II.A.2.2.b",
                  "description": "Draft a warrant article to amend the Zoning or General Bylaw to add the review process.",
                  "responsible": "POS, PCD, SBCTP",
                  "years": "2025–2026"
                }
              ],
              "partOfCarp": true,
              "carpRef": "NSCW 3.1",
              "partOfOsrp": true,
              "osrpRef": "M 2.2.2"
            },
            {
              "id": "II.A.2.3",
              "num": "3",
              "title": "Advance committee work on a Tree Protection Bylaw",
              "actions": [
                {
                  "id": "II.A.2.3.a",
                  "description": "After creating a full-time Tree Warden/Town Arborist, convene the SBCTP to review the draft Tree Protection Bylaw and consider a warrant article.",
                  "responsible": "SBCTP, POS",
                  "years": "2025–2028"
                }
              ],
              "partOfCarp": true,
              "carpRef": "NSCW 1.2"
            },
            {
              "id": "II.A.2.4",
              "num": "4",
              "title": "Integrate private-property tree protection into all Town land-use activities",
              "actions": [
                {
                  "id": "II.A.2.4.a",
                  "description": "Evaluate expanding mitigation funds for major projects to include tree canopy management funds.",
                  "responsible": "POS, PCD",
                  "years": "2022–2025"
                },
                {
                  "id": "II.A.2.4.b",
                  "description": "Integrate tree protection into health, utility, transportation, and stormwater initiatives.",
                  "responsible": "Health, PCD, DPW",
                  "years": "2022–2026"
                }
              ]
            }
          ]
        },
        {
          "id": "II.B.1",
          "sectionLetter": "B",
          "sectionName": "Encourage Private Tree Maintenance",
          "title": "Ensure sufficient management and care for existing private trees",
          "objectives": [
            {
              "id": "II.B.1.1",
              "num": "1",
              "title": "Encourage private-tree maintenance through education and outreach",
              "actions": [
                {
                  "id": "II.B.1.1.a",
                  "description": "Encourage the community and allied non-profits to advocate for protection of private trees.",
                  "responsible": "POS",
                  "years": "2022–2032",
                  "partOfOsrp": true,
                  "osrpRef": "M 4.1.1"
                },
                {
                  "id": "II.B.1.1.b",
                  "description": "Educate owners on standard tree care (web resources, workshops, volunteer opportunities).",
                  "responsible": "TPC, POS",
                  "years": "2023–2024"
                }
              ],
              "partOfOsrp": true,
              "osrpRef": "RP 6.2.2"
            }
          ]
        }
      ]
    },
    {
      "id": "III",
      "name": "Management Goals",
      "goals": [
        {
          "id": "III.A.1",
          "sectionLetter": "A",
          "sectionName": "Augment and Support Forestry Sector Operations",
          "title": "Enhance care for existing trees",
          "objectives": [
            {
              "id": "III.A.1.1",
              "num": "1",
              "title": "Complete a 7-year pruning cycle (annual for schoolyards & Beacon St inbound; biannual for heritage trees)",
              "actions": [
                {
                  "id": "III.A.1.1.a",
                  "description": "Increase Forestry Sector funding for more proactive pruning.",
                  "responsible": "POS",
                  "years": "2023"
                }
              ]
            },
            {
              "id": "III.A.1.2",
              "num": "2",
              "title": "Continue active monitoring for tree pests and diseases",
              "actions": [
                {
                  "id": "III.A.1.2.a",
                  "description": "Make the Tree Warden a full-time position.",
                  "responsible": "POS",
                  "years": "2023"
                },
                {
                  "id": "III.A.1.2.b",
                  "description": "Continue collaboration with municipalities, the Commonwealth, and the U.S. Forest Service on pest/disease monitoring.",
                  "responsible": "POS",
                  "years": "ongoing"
                }
              ]
            },
            {
              "id": "III.A.1.3",
              "num": "3",
              "title": "Increase specialized care for mature trees",
              "actions": [
                {
                  "id": "III.A.1.3.a",
                  "description": "Increase funding for decompaction, fertilization, watering, etc., for mature trees.",
                  "responsible": "POS",
                  "years": "2023"
                }
              ]
            }
          ]
        },
        {
          "id": "III.B.1",
          "sectionLetter": "B",
          "sectionName": "Adopt Resiliency-Focused Urban Forest Tree Standards",
          "title": "Adjust tree planting details and species for climate resiliency",
          "objectives": [
            {
              "id": "III.B.1.1",
              "num": "1",
              "title": "Plant species effective at mitigating climate impacts and adapting to a warmer climate",
              "actions": [
                {
                  "id": "III.B.1.1.a",
                  "description": "Revise the public tree species list (hardy in present/future temperatures; improve air quality).",
                  "responsible": "TPC, POS",
                  "years": "2022"
                },
                {
                  "id": "III.B.1.1.b",
                  "description": "Plant to 30:20:10 family:genus:species proportions using PeopleGIS analytics.",
                  "responsible": "POS",
                  "years": "2023–2032"
                },
                {
                  "id": "III.B.1.1.c",
                  "description": "Educate the public on pests and diseases to foster stewardship.",
                  "responsible": "POS",
                  "years": "2024"
                }
              ]
            },
            {
              "id": "III.B.1.2",
              "num": "2",
              "title": "Standardize public tree planting procedures",
              "actions": [
                {
                  "id": "III.B.1.2.a",
                  "description": "Improve Town tree planting standards.",
                  "responsible": "POS",
                  "years": "2023"
                },
                {
                  "id": "III.B.1.2.b",
                  "description": "Provide aftercare (watering, protection) for newly planted trees.",
                  "responsible": "POS",
                  "years": "2024"
                },
                {
                  "id": "III.B.1.2.c",
                  "description": "Promote establishment in commercial areas (25' spacing, structural soils, max tree-pit length).",
                  "responsible": "POS, PCD, Transportation, Highway",
                  "years": "2023–2025"
                }
              ]
            }
          ]
        }
      ]
    },
    {
      "id": "IV",
      "name": "Collaborative Climate Action",
      "goals": [
        {
          "id": "IV.A.1",
          "sectionLetter": "A",
          "sectionName": "Better Municipal Coordination",
          "title": "Improve intra-governmental coordination to protect the urban forest",
          "objectives": [
            {
              "id": "IV.A.1.1",
              "num": "1",
              "title": "Improve urban heat island mitigation beyond tree shading (light-color roofing, green roofs, light/permeable pavements)",
              "actions": [
                {
                  "id": "IV.A.1.1.a",
                  "description": "Share UHI-mitigation information with owners pursuing development and Design Review.",
                  "responsible": "PCD",
                  "years": "2023"
                },
                {
                  "id": "IV.A.1.1.b",
                  "description": "With appropriate staff, determine how these strategies can be encouraged.",
                  "responsible": "PCD",
                  "years": "2023"
                },
                {
                  "id": "IV.A.1.1.c",
                  "description": "Evaluate updating §5.09 Design Review to include UHI impacts as a trigger.",
                  "responsible": "PCD, POS",
                  "years": "2023"
                },
                {
                  "id": "IV.A.1.1.d",
                  "description": "Prepare realistic Design Review guidelines for difficult sites; integrate heat mitigation and greening.",
                  "responsible": "POS, PCD",
                  "years": "2022–2023"
                },
                {
                  "id": "IV.A.1.1.e",
                  "description": "Develop Design Review guidelines for large surface parking (stormwater, solar, tree protection).",
                  "responsible": "POS, SBCTP, PCD",
                  "years": "2022–2023"
                }
              ],
              "partOfCarp": true,
              "carpRef": "RAR 2.1",
              "partOfOsrp": true,
              "osrpRef": "M 2.2.1"
            },
            {
              "id": "IV.A.1.2",
              "num": "2",
              "title": "Integrate tree protection into all internal Town land-use activities",
              "actions": [
                {
                  "id": "IV.A.1.2.a",
                  "description": "Inform applicants of canopy goals and preservation/planting/protection info during 40A/40B review.",
                  "responsible": "POS, PCD",
                  "years": "2022–2032"
                },
                {
                  "id": "IV.A.1.2.b",
                  "description": "Include tree planting in town-wide health initiatives.",
                  "responsible": "Health, POS",
                  "years": "ongoing"
                },
                {
                  "id": "IV.A.1.2.c",
                  "description": "Include substantial tree-protection measures in utility and transportation projects.",
                  "responsible": "DPW",
                  "years": "ongoing"
                },
                {
                  "id": "IV.A.1.2.d",
                  "description": "Engage the Cross-Division Working Team (2020 Sustainability Report, Rec #5) to coordinate \"complete streets\" projects.",
                  "responsible": "DPW, PCD, Council on Aging, Health",
                  "years": "2024"
                }
              ]
            }
          ]
        },
        {
          "id": "IV.B.1",
          "sectionLetter": "B",
          "sectionName": "Regional Planning",
          "title": "Share tree canopy expansion goals with adjacent municipalities",
          "objectives": [
            {
              "id": "IV.B.1.1",
              "num": "1",
              "title": "Collaborate on canopy expansion with regional agencies and local municipalities",
              "actions": [
                {
                  "id": "IV.B.1.1.a",
                  "description": "Work with MAPC and directly with Boston, Cambridge, and Newton on shared/individual canopy goals.",
                  "responsible": "POS",
                  "years": "2023"
                },
                {
                  "id": "IV.B.1.1.b",
                  "description": "Collaborate with adjacent municipalities on LiDAR analysis in 3–5 years.",
                  "responsible": "POS, IT",
                  "years": "2024–2026"
                }
              ]
            }
          ]
        },
        {
          "id": "IV.C.1",
          "sectionLetter": "C",
          "sectionName": "Financing Urban Forest Initiatives",
          "title": "Identify measures to fund urban forest needs",
          "objectives": [
            {
              "id": "IV.C.1.1",
              "num": "1",
              "title": "Develop municipal funding mechanisms for forest protection, care, and expansion",
              "actions": [
                {
                  "id": "IV.C.1.1.a",
                  "description": "Evaluate funding sources (Town revenue, development review, partnerships) for forestry needs.",
                  "responsible": "SB, POS",
                  "years": "2024"
                }
              ],
              "partOfCarp": true,
              "carpRef": "CC"
            },
            {
              "id": "IV.C.1.2",
              "num": "2",
              "title": "Encourage public-private partnerships",
              "actions": [
                {
                  "id": "IV.C.1.2.a",
                  "description": "Work with local groups (GreenSpace Alliance, Emerald Necklace Conservancy, Mothers Out Front) to maintain/enhance partnerships.",
                  "responsible": "POS",
                  "years": "2023"
                }
              ],
              "partOfCarp": true,
              "carpRef": "CC",
              "partOfOsrp": true,
              "osrpRef": "M 4.1.2"
            }
          ]
        }
      ]
    },
    {
      "id": "V",
      "name": "Community Goals",
      "goals": [
        {
          "id": "V.A.1",
          "sectionLetter": "A",
          "sectionName": "Healthy Urban Forest",
          "title": "Maintain appropriate standards of care for the public urban forest",
          "objectives": [
            {
              "id": "V.A.1.1",
              "num": "1",
              "title": "Hire in-house staff and budget more hours for care, maintenance, and emergency response",
              "actions": [
                {
                  "id": "V.A.1.1.a",
                  "description": "Increase Division staffing by 2.5 FTE (Funding, p.155).",
                  "responsible": "SB, POS, Advisory Committee",
                  "years": "2025"
                },
                {
                  "id": "V.A.1.1.b",
                  "description": "Increase Capital Improvements Program (CIP) funding to enhance the urban forest.",
                  "responsible": "SB, POS, Advisory Committee",
                  "years": "2025"
                }
              ]
            },
            {
              "id": "V.A.1.2",
              "num": "2",
              "title": "Evaluate other funding sources for street tree care (insurance reimbursement, development mitigation)",
              "actions": [
                {
                  "id": "V.A.1.2.a",
                  "description": "Work with staff to evaluate methods to fund street trees.",
                  "responsible": "TPC, Town Administrator, POS",
                  "years": "2024"
                }
              ]
            },
            {
              "id": "V.A.1.3",
              "num": "3",
              "title": "Coordinate climate-mitigation planning with urban forest enhancement",
              "actions": [
                {
                  "id": "V.A.1.3.a",
                  "description": "Develop coordinated efforts across capital projects, development review, and maintenance.",
                  "responsible": "PCD, Conservation Commission, DPW, SB Climate Action Committee",
                  "years": "2024"
                }
              ]
            },
            {
              "id": "V.A.1.4",
              "num": "4",
              "title": "Reduce conflicts between utilities and the urban forest",
              "actions": [
                {
                  "id": "V.A.1.4.a",
                  "description": "Continue testing/monitoring for natural gas leaks and their impact on trees.",
                  "responsible": "DPW, Fire, National Grid",
                  "years": "ongoing"
                },
                {
                  "id": "V.A.1.4.b",
                  "description": "Discuss procedures and tree concerns with private overhead-utility companies.",
                  "responsible": "DPW, private utilities",
                  "years": "ongoing"
                }
              ]
            }
          ]
        },
        {
          "id": "V.B.1",
          "sectionLetter": "B",
          "sectionName": "Education, Outreach and Advocacy",
          "title": "Increase engagement with the community",
          "objectives": [
            {
              "id": "V.B.1.1",
              "num": "1",
              "title": "Collaborate with groups to support trees, open space, and sustainability",
              "actions": [
                {
                  "id": "V.B.1.1.a",
                  "description": "Coordinate activities (tree-ID walks, volunteering, citizen science).",
                  "responsible": "POS, Conservation Commission, TPC, ODI, SB Climate Action Committee, and community partners",
                  "years": "2022"
                },
                {
                  "id": "V.B.1.1.b",
                  "description": "Develop strategies to reach residents not affiliated with these groups.",
                  "responsible": "POS, Conservation Commission, and partners",
                  "years": "2023"
                }
              ]
            },
            {
              "id": "V.B.1.2",
              "num": "2",
              "title": "Increase public awareness (print/online media, events, annual reporting of achievements)",
              "actions": [
                {
                  "id": "V.B.1.2.a",
                  "description": "Promote activities and opportunities through social media and newsletters.",
                  "responsible": "POS",
                  "years": "ongoing"
                },
                {
                  "id": "V.B.1.2.b",
                  "description": "Prioritize the outreach strategies rated most desirable in the community survey (Appendix B).",
                  "responsible": "POS",
                  "years": "2022"
                }
              ]
            },
            {
              "id": "V.B.1.3",
              "num": "3",
              "title": "Educate the public on climate change impacts on health and the urban forest",
              "actions": [
                {
                  "id": "V.B.1.3.a",
                  "description": "Work with the SB Climate Action Committee and Climate Action Brookline to disseminate information.",
                  "responsible": "SB Climate Action Committee, Climate Action Brookline, SB, POS",
                  "years": "2023"
                },
                {
                  "id": "V.B.1.3.b",
                  "description": "Maintain the project website with the completed report and interviews.",
                  "responsible": "POS",
                  "years": "2022"
                },
                {
                  "id": "V.B.1.3.c",
                  "description": "Direct advertising to low-canopy areas and populations vulnerable to climate change.",
                  "responsible": "POS",
                  "years": "2022"
                }
              ]
            }
          ]
        }
      ]
    }
  ]
};

export const CARP_PATH = '/carp-2026';
export const OSRP_PATH = '/open-space';
